import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Spinner } from '@/components/ui'
import { updateLocation, createLocation, emptyLocation } from './locationsSlice'
import { useBienContext } from '../biens/BienContext'

import NewLocationForm from './NewLocationForm'
import CloseLocationForm from './CloseLocationForm'
import LocationValuesForm from './LocationValuesForm'
import { FORM_TYPES, LocationFormProvider } from './LocationFormContext'

import useFormConfirmation from "@/hooks/useFormConfirmation"

export default ({formType = FORM_TYPES.EDIT, onClose = () => {}, onSuccess = () => {}}) => {
  const dispatch = useDispatch()
  const { bien } = useBienContext()
  const location = useSelector((state) => state.locations.inEdition || state.locations.current || emptyLocation)
  const errors = useSelector((state) => state.locations.requestStatus.errors)
  const isCreating = useSelector((state) => state.locations.loadingStatus.create)
  const isUpdating = useSelector((state) => state.locations.loadingStatus.update)
  const isLoading = isCreating || isUpdating
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { handleSubmit, 
    watch,
    setValue, getValues, reset, 
    control, formState: { errors: formErrors }
  } = useForm({
    defaultValues: location,
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const onReset = () => {
    reset(location)
  }

  /**
   * Récupère le message d'erreur pour un champ donné
   * Combine les erreurs de validation côté client et les erreurs API
   *
   * @param {string} fieldName - Nom du champ
   * @returns {string} Message d'erreur ou chaîne vide
   */
  const getErrorMessage = (fieldName) =>
    formErrors?.[fieldName]?.message || errors?.[fieldName] || ''

  /**
   * Détermine si un champ a une erreur
   *
   * @param {string} fieldName - Nom du champ
   * @returns {boolean} true si le champ a une erreur, false sinon
   */
  const hasError = (fieldName) =>
    !!formErrors?.[fieldName] || !!errors?.[fieldName]

  // useFormConfirmation feature
  // ajoute les states, le useEffect pour surveiller l'état du formulaire
  // et s'assurer s'il a été modifié ou pas pour afficher une boîte de 
  // confirmation lorsque le bouton "Annuler" est cliqué.
  const {
    handleFormCancel,
    setInitialLoadComplete,
    setInitialValues,
    setFormIsDirty,
    FormConfirmDialog
  } = useFormConfirmation({
    watch,
    getValues,
    onReset,
    onClose
  })

  // Synchroniser le formulaire lorsque location change 
  useEffect(() => {
    onReset()
    // if(location.locataires) {
    //   setValue("locataires", location.locataires.map( c => ({...c, contact_id: c.id}) ))
    // }
    // else {
    //   setValue("locataires", [])
    // }
    setInitialValues(getValues())
    setInitialLoadComplete(true)
    setFormIsDirty(false) // Réinitialiser l'état dirty après le chargement initial
  }, [location]) 

  // Effet pour détecter les changements dans l'état de chargement
  useEffect(() => {
    if (isSubmitting && !isUpdating && !isCreating) {
      setIsSubmitting(false)
      if (!errors || Object.keys(errors).length === 0) {
        onSuccess()
      }
    }
  }, [isUpdating, isCreating, errors, isSubmitting, onSuccess, dispatch])

  /**
   * Gère la soumission du formulaire
   * Dispatche l'action appropriée (create ou update) selon si le bien existe déjà
   *
   * @param {Object} formData - Données du formulaire
   */
  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true)

      if (location.id) {
        await dispatch(updateLocation({bien_id: bien.id, id: location.id, data: formData, formType}))
      } else {
        await dispatch(createLocation({data: formData, bien_id: bien.id, formType}))
      }
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  const contextValues = {
    hasError,
    getErrorMessage,
    getValues,
    control,
    location,
    isLoading,
    formErrors,
    errors,
    watch
  }

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">
            { (() => {
                switch(formType) {
                  case FORM_TYPES.NEW:
                    return "Entrée en location"
                  case FORM_TYPES.CLOSE:
                    return "Clôturer la location"
                  case FORM_TYPES.CLOSE_EDIT:
                    return "Modifier la location"
                  case FORM_TYPES.EDIT:
                  default:
                    return "Saisir la location"
                }
            })() }
          </h2>
        </div>

        <div className="card bg-base-100 border border-base-200 rounded-none mb-4 min-h-[450px] max-h-[calc(80vh-150px)] overflow-y-auto">
          <div className="p-4">
            <div className="card-body p-4">
              <LocationFormProvider {...contextValues}>
                {(() => {
                  switch(formType) {
                    case FORM_TYPES.NEW:
                      return <NewLocationForm />
                    case FORM_TYPES.CLOSE:
                      return <CloseLocationForm />
                    case FORM_TYPES.CLOSE_EDIT:
                      return <CloseLocationForm formType="edit" />
                    case FORM_TYPES.EDIT:
                    default:
                      return <LocationValuesForm />
                  }
                })()}
              </LocationFormProvider>
            </div>
          </div>
        </div>

        <div className="flex justify-between">
          <button
            type="button"
            className="btn btn-outline"
            onClick={handleFormCancel}
          >
            Annuler
          </button>
          <button
            type="submit"
            className={`btn ${isLoading ? 'loading' : ''} btn-primary text-white p-2 mt-4`}
            disabled={isLoading}
          >
            {isLoading ? <Spinner/> : 'Enregistrer'}
          </button>
        </div>
      </form>
      <FormConfirmDialog />
    </div>
  )
}